/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { createTheme, makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Container, ThemeProvider, Typography } from '@material-ui/core';
import Layout from '../../layout/Layout';
import { postMergeTheme, preMergeTheme } from '../theming/theme';
import { PermissionError } from './PermissionError';
import ErrorIcon from '../../components/icons/ErrorIcon';

const useStyles = theme =>
  makeStyles(() => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      color: theme.palette.secondary.contrastText,
      paddingTop: '15%',
    },
    title: {
      fontWeight: '500',
      fontSize: '4rem',
      paddingBottom: '1rem',
      zIndex: '1',
      position: 'relative',
      [theme.breakpoints.up('xl2')]: {
        fontSize: '6.25rem',
      },
    },
    errorMessage: {
      fontSize: '18px',
    },
    genericMessage: {
      fontSize: '25px',
      lineHeight: '39px',
    },
    cancelIcon: {
      color: theme.palette.footerBackground,
      width: '35rem',
      height: '35rem',
      position: 'fixed',
      right: '0',
      bottom: '1%',
    },
    textDiv: {
      zIndex: '1',
      position: 'relative',
    },
    errorBox: {
      position: 'relative',
      maxWidth: '80rem',
      zIndex: 2,
    },
    link: {
      textDecoration: 'none',
    },
    buttonBox: {
      marginTop: '2.875rem',
    },
  }));

export default function ErrorPage({ error }) {
  let theme = createTheme(preMergeTheme, { ...preMergeTheme, bgHome: preMergeTheme.palette.white });
  theme = createTheme(theme, postMergeTheme(theme));

  const classes = useStyles(theme)();
  const { t } = useTranslation();

  const genericMessage = t('error.page.generic.message');
  let title = t('error.page.generic.title');

  if (error instanceof PermissionError) {
    title = t('messages.permissions.error.user.profile');
  }

  let errorMessage = null;

  if (typeof error === 'string') {
    errorMessage = error;
  } else if (typeof error === 'object') {
    if (error.data?.message) {
      errorMessage = error.data?.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <Container maxWidth="xl" className={classes.container}>
          <div className={classes.textDiv} style={{ zIndex: '1', position: 'relative' }}>
            <Typography className={classes.title} variant="h1">
              <span className={classes.title}>{t('error.page.title')}</span>
            </Typography>

            <p className={classes.genericMessage}>{title}</p>
            <p className={classes.genericMessage}>
              <strong>{genericMessage}</strong>
            </p>
          </div>
          {errorMessage && <span className={classes.errorMessage}>{errorMessage}</span>}

          <ErrorIcon className={classes.cancelIcon} />
        </Container>
      </Layout>
    </ThemeProvider>
  );
}
